import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { DATA_KEYS, EMPTY_VALUE, TONE } from '../../data/constants'
import Chip from '../../components/Chip/Chip'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubBlock from '../../components/SubBlock/SubBlock'

const getGroupLifecyclePolicyLifetimeDays = (tenant) => {
  const lifetimeDays = tenant?.groupLifecyclePolicyLifetimeDays
  return Number.isFinite(lifetimeDays) ? lifetimeDays : null
}

const getTableFooter = ({ shown, total }) => {
  if (shown === 0 || total <= shown) return null

  return <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.TABLE_FOOTER" values={{ shown, total }} />
}

const getGroupLifecyclePolicyIsAssessed = (dataAvailability = []) =>
  dataAvailability.find((entry) => entry.dataKey === DATA_KEYS.GROUP_EXPIRY_POLICY)?.status === 'assessed'

export default function GroupGovernance({ summary, tenant = null, dynamicGroups, score, dataAvailability = [] }) {
  const dynamicWithRules = summary.groups?.dynamicWithMembershipRules ?? dynamicGroups.length
  const dynamicCanIncludeGuests =
    summary.groups?.dynamicCanIncludeGuests ?? dynamicGroups.filter((group) => group.canIncludeGuests).length
  const dynamicGroupRows = dynamicGroups.map((group) => ({ ...group, key: group.entityId }))
  const groupLifecyclePolicyLifetimeDays = getGroupLifecyclePolicyLifetimeDays(tenant)
  const groupLifecyclePolicyIsAssessed = getGroupLifecyclePolicyIsAssessed(dataAvailability)
  const hasNoGroupLifecyclePolicy = groupLifecyclePolicyIsAssessed && groupLifecyclePolicyLifetimeDays === null
  let groupLifecyclePolicyValue = EMPTY_VALUE

  if (groupLifecyclePolicyLifetimeDays !== null) {
    groupLifecyclePolicyValue = (
      <FormattedMessage
        id="AI_RISK_REPORT.DATA.GROUPS.STAT_LIFECYCLE_DAYS"
        values={{ days: groupLifecyclePolicyLifetimeDays }}
      />
    )
  } else if (hasNoGroupLifecyclePolicy) {
    groupLifecyclePolicyValue = <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.STAT_LIFECYCLE_NONE" />
  }

  const stats = [
    {
      key: 'total',
      value: summary.groups?.microsoft365 ?? '-',
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.STAT_TOTAL" />,
    },
    {
      key: 'expirationPolicy',
      value: groupLifecyclePolicyValue,
      tone: hasNoGroupLifecyclePolicy ? TONE.DANGER : undefined,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.STAT_LIFECYCLE_LABEL" />,
    },
    {
      key: 'guestInclusion',
      value: dynamicCanIncludeGuests,
      denom: dynamicWithRules,
      tone: dynamicCanIncludeGuests > 0 ? TONE.DANGER : TONE.OK,
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.STAT_GUEST_INCL" />,
    },
  ]

  const columns = [
    {
      key: 'group',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.COL_GROUP" />,
      render: (row) => <span className="text-body fw-bold">{row.displayName}</span>,
      sortable: true,
      sortValue: (row) => (row.displayName || '').toLowerCase(),
    },
    {
      key: 'members',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.COL_MEMBERS" />,
      render: (row) => row.memberCount ?? '-',
      sortable: true,
      sortValue: (row) => row.memberCount ?? -1,
    },
    {
      key: 'rule',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.COL_RULE" />,
      render: (row) => (row.membershipRule ? <code>{row.membershipRule}</code> : '-'),
      sortable: true,
      sortValue: (row) => row.membershipRule || '',
    },
    {
      key: 'guests',
      header: <FormattedMessage id="AI_RISK_REPORT.DATA.GROUPS.COL_GUESTS" />,
      render: (row) => (
        <Chip tone={row.canIncludeGuests ? TONE.DANGER : TONE.OK}>
          <FormattedMessage id={row.canIncludeGuests ? 'AI_RISK_REPORT.LABEL.NO' : 'AI_RISK_REPORT.LABEL.YES'} />
        </Chip>
      ),
      sortable: true,
      sortValue: (row) => (row.canIncludeGuests ? 1 : 0),
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.DATA.GROUPS.TITLE"
      subtitleId="AI_RISK_REPORT.DATA.GROUPS.SUBTITLE"
      score={score.value}
      tone={score.tone}
      stats={stats}
    >
      <ReportTable
        columns={columns}
        rows={dynamicGroupRows}
        footer={getTableFooter({ shown: dynamicGroupRows.length, total: dynamicWithRules })}
        empty={<EmptyState messageId="AI_RISK_REPORT.NO_DYNAMIC_GROUPS" />}
      />
    </SubBlock>
  )
}

const summaryPropType = PropTypes.shape({
  groups: PropTypes.shape({
    total: PropTypes.number,
    microsoft365: PropTypes.number,
    dynamicWithMembershipRules: PropTypes.number,
    dynamicCanIncludeGuests: PropTypes.number,
  }),
})

GroupGovernance.propTypes = {
  summary: summaryPropType.isRequired,
  tenant: PropTypes.shape({
    groupLifecyclePolicyLifetimeDays: PropTypes.number,
  }),
  dynamicGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})),
}
